import { useState, useEffect, useRef } from "react";
import { CardSkeleton } from "../components/Skeletons";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Settings, Building2, Loader2, Upload, Check, Crown, Image, Download, FileSpreadsheet, ArrowRight, CheckCircle2, Home, Type, Bot, Save, ChevronDown, Palette, Plug } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { SocietesSettings } from "../components/SocietesSettings";
import MyModulesWidget from "../components/MyModulesWidget";
import AircallTranscriptionUsageCard from "../components/aircall/AircallTranscriptionUsageCard";
import { forceClearCacheAndReload } from "../components/VersionUpdateBanner";
import { BrandingConfigurator } from "../components/BrandingConfigurator";

export default function TenantSettingsPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/tenant/info").then(r => {
      setTenant(r.data?.tenant);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadSuccess(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/tenant/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data?.logo_url) {
        setTenant(t => ({ ...t, logo: r.data.logo_url }));
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 3000);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'upload");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (loading) return <div className="p-6 lg:p-10 space-y-4" data-testid="tenant-settings-loading"><CardSkeleton /><CardSkeleton /></div>;
  if (!tenant) {
    // Master Blitzz admins (no tenant_id) hit this branch.
    if (!user?.tenant_id && (user?.role === "admin" || user?.role === "superadmin")) {
      return (
        <div className="p-6 max-w-3xl mx-auto" data-testid="tenant-settings-page">
          <PageHeader
            icon={<Settings />}
            title="Paramètres"
            description="Configuration tenant"
            testId="tenant-settings-header-empty"
          />
          <div className="bg-amber-500/10 border border-amber-500/20 text-amber-200 rounded-xl p-5 flex items-start gap-3" data-testid="master-admin-banner">
            <Crown className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-400" />
            <div className="text-sm">
              <strong className="block mb-1 text-base text-white">Compte administrateur du groupe</strong>
              Cette page concerne les paramètres d'une organisation. En tant qu'administrateur du groupe, utilisez les Paramètres généraux.
              <div className="mt-3 flex flex-wrap gap-2">
                <a href="/admin/settings" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#f7c935] text-[#0b0811] font-bold text-xs hover:bg-[#e6b825] transition-colors" data-testid="goto-admin-settings">
                  Ouvrir les Paramètres <ArrowRight className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <div className="p-6 text-center text-slate-500">Aucun tenant associe</div>;
  }

  const PLAN_COLORS = { starter: "#64748b", pro: "#3b82f6", enterprise: "#f7c935" };

  // Allow tenant configuration for any staff role that operates inside a tenant scope.
  // Master Blitzz admins (no tenant_id) are excluded — they edit per-tenant from MasterAdminPage.
  const canManageTenant = !!user?.tenant_id && ["tenant_admin", "tenant_owner", "owner", "admin", "superadmin", "manager"].includes(user?.role);

  const MODULE_LABELS = {
    clients: "Clients", prospects: "Prospects", crm: "CRM Pipeline", facturation: "Facturation",
    pilotage: "Pilotage", marketing_social: "Reseaux Sociaux", marketing_mailing: "Mailing",
    marketing_google: "Google Ads", builder: "Builder", messages: "Messagerie",
    documents: "Documents", ai_agents: "Agents IA", agenda: "Agenda",
    depenses: "Depenses", encaissements: "Encaissements", tresorerie: "Tresorerie",
    agent_ia: "Agent IA", outils: "Equipements", equipe: "Equipe", qualiopi: "Qualiopi",
    formations: "Formations",
    recherche_ia: "Recherche IA", conception_docs: "Conception Docs IA",
    holding: "Holding", societes: "Gestion Societes",
    analytics: "Analytics & Reporting", briefs_hebdo: "Briefs Marketing Hebdo",
    // Mon Espace
    espace_taches: "Mes Taches", espace_equipement: "Mon Equipement",
    espace_depenses: "Mes Depenses", espace_documents: "Mes Documents",
    espace_personnel: "Mon Espace",
    // Formation granulaire
    formation_bibliotheque: "Formation - Bibliotheque",
    formation_lms: "Formation - Sessions & LMS",
    formation_inscriptions: "Formation - Inscriptions",
    formation_bpf: "Formation - Stats BPF",
    formation_conformite: "Formation - Conformite Qualiopi",
    formation_evaluations: "Formation - Evaluations",
    formation_catalogue: "Formation - Catalogue public",
    formation_edof: "Formation - Export EDOF",
    formation_certifications: "Formation - Certifications RS/RNCP",
    certifications: "Examens certifiants (sessions, sujets IA, jury)",
    // Facturation B2B
    ppf_factur_x: "Facturation B2B - PPF Factur-X",
    e_invoices: "Facturation B2B - E-invoices",
    // Lot 1+2 (Iter 233-236)
    affiliate: "Affiliation longue durée",
    yousign: "Signature eIDAS Yousign",
    intel_agent: "Agent IA enquêteur",
    welcome_stagiaire: "Welcome Stagiaire J-7 + Quiz",
    time_tracking: "Pointage GPS + WhatsApp",
    workflows: "Workflows & Automations",
    messaging_meta: "Messagerie Meta (WA/FB/IG)",
    // Iter 245 — Commandes & Stocks
    inventory: "Commandes & Stocks (Shopify)",
    // Iter 248-253 — Vente Augmentée IA
    aircall_translator: "Traducteur d'appels (10 langues)",
    commercial_panel: "Mon panel commercial",
    objection_cookbook: "Cookbook objections",
    coach_stats: "Stats Coach IA",
    weekly_digest: "Copilote manager hebdo",
    comptabilite: "Comptabilité générale & consolidation groupe",
    social: "Social (paie & déclarations)",
    // Intégrations
    integration_resend: "Integration Email",
    integration_aircall: "Integration Aircall",
    integration_branding: "Marque blanche",
  };

  return (
    <div className="p-6 max-w-[900px] mx-auto" data-testid="tenant-settings-page">
      <PageHeader
        icon={<Settings />}
        title="Paramètres"
        description="Configuration de votre tenant : logo, modules, intégrations, abonnement"
        testId="tenant-settings-header"
      />

      {/* Logo Upload */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6" data-testid="logo-section">
        <div className="flex items-center gap-2 mb-4">
          <Image className="w-4 h-4 text-[#f7c935]" />
          <h3 className="text-sm font-bold text-white">Logo de l'entreprise</h3>
        </div>
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 rounded-xl border-2 border-dashed border-white/10 flex items-center justify-center bg-[#0b0811] overflow-hidden flex-shrink-0" data-testid="logo-preview">
            {tenant.logo ? (
              <img src={tenant.logo} alt="Logo" className="w-full h-full object-contain p-1" />
            ) : (
              <Building2 className="w-10 h-10 text-[#888194]/30" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs text-[#888194] mb-3">Formats acceptes : PNG, JPG, SVG. Taille max : 2 Mo.<br/>Affiche sur la page de connexion et les documents.</p>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
            <div className="flex items-center gap-2">
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} size="sm" className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs" data-testid="upload-logo-btn">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                {uploading ? "Upload..." : tenant.logo ? "Changer le logo" : "Uploader un logo"}
              </Button>
              {uploadSuccess && <span className="text-xs text-green-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Logo mis a jour</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Tenant Info */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-xl bg-[#0b0811] flex items-center justify-center border border-white/5">
            {tenant.logo ? <img src={tenant.logo} alt="" className="w-14 h-14 rounded-lg object-contain" /> : <Building2 className="w-8 h-8 text-[#f7c935]" />}
          </div>
          <div>
            <h2 className="text-xl font-black text-white">{tenant.name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: (PLAN_COLORS[tenant.plan] || "#64748b") + "20", color: PLAN_COLORS[tenant.plan] || "#64748b" }}>
                {tenant.plan?.toUpperCase()}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${tenant.status === "active" ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                {tenant.status === "active" ? "Actif" : "Desactive"}
              </span>
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500">ID: {tenant.tenant_id} | Cree le {new Date(tenant.created_at).toLocaleDateString("fr-FR")}</div>
      </div>

      {/* Active Modules */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6" data-testid="tenant-active-modules">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <Crown className="w-4 h-4 text-[#f7c935]" /> Modules actifs
        </h3>
        <div className="flex flex-wrap gap-2">
          {(tenant.active_modules || []).filter(mod => mod !== "import_ia").map(mod => (
            <span key={mod} className="px-3 py-1.5 rounded-lg bg-[#f7c935]/10 text-[#f7c935] text-xs font-bold border border-[#f7c935]/20">
              {MODULE_LABELS[mod] || mod}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-3">Contactez Blitzz pour modifier vos modules ou changer de plan.</p>
      </div>

      {/* Welcome Text Config */}
      {canManageTenant && <WelcomeTextConfig api={api} tenant={tenant} setTenant={setTenant} />}

      {/* Info banner for Blitzz master admin (no tenant_id) */}
      {!user?.tenant_id && (user?.role === "admin" || user?.role === "superadmin") && (
        <div className="bg-amber-500/10 border border-amber-500/20 text-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="master-admin-banner">
          <Crown className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-400" />
          <div className="text-xs">
            <strong className="block mb-0.5">Compte administrateur Blitzz detecte</strong>
            Les panneaux <strong>Configurateur visuel</strong>, <strong>API & Integrations</strong>, <strong>Mon abonnement</strong> et <strong>Agent IA</strong> sont configures par tenant. Pour les modifier, connectez-vous avec un compte <em>tenant_admin</em> du tenant concerne, ou allez dans <a href="/admin/master/tenants" className="underline font-bold text-amber-100">Master Admin → Tenants</a>.
          </div>
        </div>
      )}

      {/* Configurateur visuel (couleurs, logo, marque blanche) - inline */}
      {canManageTenant && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6" data-testid="tenant-branding-section">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="w-4 h-4 text-[#f7c935]" />
            <h3 className="text-sm font-bold text-white">Configurateur visuel</h3>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#f7c935]/15 text-[#f7c935] uppercase font-bold">Couleurs · Logo · Marque blanche</span>
          </div>
          <BrandingConfigurator compact />
        </div>
      )}

      {/* API et Integrations (Resend, Aircall, ...) */}
      {canManageTenant && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6" data-testid="tenant-integrations-banner">
          <div className="flex items-center gap-2 mb-2">
            <Plug className="w-4 h-4 text-[#38bdf8]" />
            <h3 className="text-sm font-bold text-white">API & Integrations</h3>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#38bdf8]/15 text-[#38bdf8] uppercase font-bold">Resend · Aircall</span>
          </div>
          <p className="text-[11px] text-slate-500 mb-3">Resend (emails transactionnels brandes), Aircall (click-to-call + resume IA des appels dans la timeline CRM). Configurez vos cles API pour activer ces services.</p>
          <a href="/tenant/integrations" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#38bdf8]/10 text-[#38bdf8] font-bold border border-[#38bdf8]/20 hover:bg-[#38bdf8]/20 transition-colors" data-testid="open-integrations-settings">
            Configurer les API <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      )}

      {/* Whisper transcription usage + auto-toggle */}
      {canManageTenant && (
        <div className="mb-6">
          <AircallTranscriptionUsageCard />
        </div>
      )}

      {/* My Modules — pretty summary widget */}
      <MyModulesWidget />

      {/* Agent IA Config */}
      {canManageTenant && <AgentConfigPanel api={api} tenant={tenant} />}

      {/* Societes Management */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6">
        <SocietesSettings />
      </div>

      {/* Account Info */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6">
        <h3 className="text-sm font-bold text-white mb-4">Mon compte</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-sm text-slate-400">Nom</span>
            <span className="text-sm text-white font-medium">{user?.name}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-sm text-slate-400">Email</span>
            <span className="text-sm text-white font-medium">{user?.email}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-sm text-slate-400">Role</span>
            <span className="text-sm text-white font-medium">{user?.role === "tenant_admin" ? "Administrateur" : "Membre"}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-slate-400">Max membres</span>
            <span className="text-sm text-white font-medium">{tenant.max_members}</span>
          </div>
        </div>
      </div>

      {/* Import/Export — only if tenant has facturation or crm */}
      {((tenant.active_modules || []).includes("facturation") || (tenant.active_modules || []).includes("crm")) && (
        <TenantImportExport api={api} />
      )}
    </div>
  );
}

function TenantImportExport({ api }) {
  const [activeTab, setActiveTab] = useState("clients");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const tabs = [
    { key: "clients", label: "Clients" },
    { key: "crm", label: "Echanges CRM" },
    { key: "invoices", label: "Factures" },
  ];
  const endpoints = {
    clients: { analyze: "/import/clients/analyze", execute: "/import/clients/execute" },
    crm: { analyze: "/import/crm-exchanges/analyze", execute: "/import/crm-exchanges/execute" },
    invoices: { analyze: "/import/invoices/analyze", execute: "/import/invoices/execute" },
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzing(true); setError(""); setResult(null); setAnalysis(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(endpoints[activeTab].analyze, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(r.data);
    } catch (err) { setError(err.response?.data?.detail || "Erreur"); }
    setAnalyzing(false);
  };
  const executeImport = async () => {
    if (!analysis) return;
    setExecuting(true); setError("");
    try { const r = await api.post(endpoints[activeTab].execute, analysis); setResult(r.data); setAnalysis(null); }
    catch (err) { setError(err.response?.data?.detail || "Erreur"); }
    setExecuting(false);
  };
  const handleExport = async (type) => {
    setExporting(true);
    try {
      const r = await api.get(`/export/${type}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `export_${type}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (err) { alert("Erreur: " + (err.response?.data?.detail || err.message)); }
    setExporting(false);
  };

  return (
    <div className="mt-6 space-y-4" data-testid="tenant-import-export">
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
          <Upload className="w-4 h-4 text-[#f7c935]" /> Import / Export
        </h3>
        <div className="flex gap-2 mb-4">
          {tabs.map(t => (
            <button key={t.key} onClick={() => { setActiveTab(t.key); setAnalysis(null); setResult(null); setError(""); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${activeTab === t.key ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-400"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {!analysis && !result && (
          <label className="block cursor-pointer">
            <input type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={handleUpload} className="hidden" />
            <div className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center hover:border-[#f7c935]/30">
              {analyzing ? <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto" /> : (
                <><FileSpreadsheet className="w-6 h-6 text-slate-500 mx-auto mb-1" /><span className="text-xs text-slate-500">Selectionnez un fichier</span></>
              )}
            </div>
          </label>
        )}
        {analysis && !result && (
          <div className="space-y-3">
            <div className="bg-[#0b0811] rounded-lg p-3 text-xs text-slate-400">{analysis.total_rows || analysis.count || "?"} enregistrements detectes</div>
            <div className="flex gap-2">
              <Button onClick={executeImport} disabled={executing} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold text-xs">
                {executing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ArrowRight className="w-3 h-3 mr-1" />} Importer
              </Button>
              <Button onClick={() => setAnalysis(null)} variant="outline" className="border-white/10 text-white text-xs">Annuler</Button>
            </div>
          </div>
        )}
        {result && (
          <div className="bg-green-500/10 rounded-lg p-3 text-xs text-green-400 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> {result.created || result.count || 0} importe(s)
            <Button onClick={() => { setResult(null); setAnalysis(null); }} variant="outline" className="ml-auto border-white/10 text-white text-xs h-7">Nouvel import</Button>
          </div>
        )}
        {error && <div className="bg-red-500/10 rounded-lg p-2 text-xs text-red-400 mt-2">{error}</div>}
      </div>
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-4"><Download className="w-4 h-4 text-blue-400" /> Export</h3>
        <div className="grid grid-cols-3 gap-3">
          {[{ key: "clients", label: "Clients" }, { key: "invoices", label: "Factures" }, { key: "depenses", label: "Depenses" }].map(e => (
            <button key={e.key} onClick={() => handleExport(e.key)} disabled={exporting}
              className="p-3 rounded-lg border border-white/5 bg-[#0b0811] hover:border-[#f7c935]/20 text-left">
              <div className="text-xs font-bold text-white">{e.label}</div>
              <div className="flex items-center gap-1 mt-2 text-[10px] text-[#f7c935]">
                {exporting ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Download className="w-2.5 h-2.5" />} CSV
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


function WelcomeTextConfig({ api, tenant, setTenant }) {
  const [adminText, setAdminText] = useState(tenant?.welcome_text_admin || "");
  const [clientText, setClientText] = useState(tenant?.welcome_text_client || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenant/home-config", { welcome_text_admin: adminText, welcome_text_client: clientText });
      setTenant(t => ({ ...t, welcome_text_admin: adminText, welcome_text_client: clientText }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6" data-testid="welcome-text-config">
      <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
        <Home className="w-4 h-4 text-[#f7c935]" /> Textes de la page d'accueil
      </h3>
      <div className="space-y-4">
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block flex items-center gap-1.5">
            <Type className="w-3 h-3" /> Message d'accueil equipe (admin / membres)
          </label>
          <textarea
            value={adminText}
            onChange={e => setAdminText(e.target.value)}
            placeholder="Ex: Bienvenue dans votre espace de gestion..."
            rows={3}
            className="w-full bg-[#0b0811] border border-white/10 rounded-lg p-3 text-sm text-white placeholder-slate-600 resize-none focus:outline-none focus:border-[#f7c935]/30"
            data-testid="welcome-admin-text"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block flex items-center gap-1.5">
            <Type className="w-3 h-3" /> Message d'accueil clients
          </label>
          <textarea
            value={clientText}
            onChange={e => setClientText(e.target.value)}
            placeholder="Ex: Bienvenue ! Retrouvez vos prestations et documents ici..."
            rows={3}
            className="w-full bg-[#0b0811] border border-white/10 rounded-lg p-3 text-sm text-white placeholder-slate-600 resize-none focus:outline-none focus:border-[#f7c935]/30"
            data-testid="welcome-client-text"
          />
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs h-9" data-testid="save-welcome-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
            Enregistrer
          </Button>
          {saved && <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Enregistre</span>}
        </div>
      </div>
    </div>
  );
}


function AgentConfigPanel({ api, tenant }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    api.get("/ai/agents").then(r => {
      setAgents(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const savePrompt = async (agentType, prompt) => {
    setSaving(agentType);
    try {
      await api.put(`/ai/agents/${agentType}`, { system_prompt: prompt });
      setSaved(agentType);
      setTimeout(() => setSaved(null), 2000);
    } catch (e) { alert("Erreur: " + (e.response?.data?.detail || e.message)); }
    setSaving(null);
  };

  const toggleFunction = async (agentType, fnId) => {
    const agent = agents.find(a => a.type === agentType);
    if (!agent) return;
    const updated = (agent.functions || []).map(f => f.id === fnId ? { ...f, enabled: !f.enabled } : f);
    try {
      await api.put(`/ai/agents/${agentType}`, { functions: updated });
      setAgents(agents.map(a => a.type === agentType ? { ...a, functions: updated } : a));
    } catch {}
  };

  const toggleAgent = async (agentType) => {
    const agent = agents.find(a => a.type === agentType);
    if (!agent) return;
    try {
      await api.put(`/ai/agents/${agentType}`, { enabled: !agent.enabled });
      setAgents(agents.map(a => a.type === agentType ? { ...a, enabled: !a.enabled } : a));
    } catch {}
  };

  const relevantAgents = agents;

  if (loading) return null;
  if (relevantAgents.length === 0) return null;

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6" data-testid="tenant-agent-config">
      <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
        <Bot className="w-4 h-4 text-[#f7c935]" /> Configuration des Agents IA
      </h3>
      <p className="text-[10px] text-slate-500 mb-4">Personnalisez le comportement et les prompts de chaque agent</p>

      <div className="space-y-2">
        {relevantAgents.map(agent => (
          <div key={agent.type} className="border border-white/5 rounded-lg overflow-hidden" data-testid={`tenant-agent-${agent.type}`}>
            {/* Header */}
            <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/[0.02]" onClick={() => setExpandedAgent(expandedAgent === agent.type ? null : agent.type)}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${agent.color || "#f7c935"}15` }}>
                <Bot className="w-4 h-4" style={{ color: agent.color || "#f7c935" }} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-bold text-white">{agent.name}</span>
                <p className="text-[9px] text-slate-500 truncate">{agent.description}</p>
              </div>
              <button onClick={(e) => { e.stopPropagation(); toggleAgent(agent.type); }}
                className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${agent.enabled ? "bg-green-500" : "bg-slate-600"}`}
                data-testid={`tenant-toggle-agent-${agent.type}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${agent.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
              <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform flex-shrink-0 ${expandedAgent === agent.type ? "rotate-180" : ""}`} />
            </div>

            {/* Expanded */}
            {expandedAgent === agent.type && (
              <div className="px-3 pb-3 border-t border-white/5 pt-3">
                {/* Functions toggles */}
                <div className="mb-3">
                  <span className="text-[9px] text-slate-500 uppercase font-bold">Fonctions</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1.5">
                    {(agent.functions || []).map(fn => (
                      <button key={fn.id} onClick={() => toggleFunction(agent.type, fn.id)}
                        className="flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-white/[0.03]" data-testid={`tenant-fn-${agent.type}-${fn.id}`}>
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${fn.enabled ? "bg-green-500 border-green-500" : "border-slate-600"}`}>
                          {fn.enabled && <CheckCircle2 className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <span className={`text-[10px] ${fn.enabled ? "text-slate-300" : "text-slate-500"}`}>{fn.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* System prompt */}
                <div>
                  <span className="text-[9px] text-slate-500 uppercase font-bold">Prompt systeme</span>
                  <textarea
                    value={agent.system_prompt || ""}
                    onChange={e => setAgents(agents.map(a => a.type === agent.type ? { ...a, system_prompt: e.target.value } : a))}
                    className="w-full mt-1.5 bg-[#0b0811] border border-white/10 text-[11px] text-white rounded-lg px-3 py-2 h-32 resize-y focus:border-[#f7c935] focus:outline-none"
                    placeholder="Instructions pour l'agent..."
                    data-testid={`tenant-prompt-${agent.type}`}
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <Button onClick={() => savePrompt(agent.type, agent.system_prompt)}
                      disabled={saving === agent.type}
                      className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] text-[10px] font-bold h-7 px-3"
                      data-testid={`tenant-save-prompt-${agent.type}`}>
                      {saving === agent.type ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                      Sauvegarder
                    </Button>
                    {saved === agent.type && <span className="text-[10px] text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Enregistre</span>}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Maintenance / Cache */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6 mt-6" data-testid="maintenance-section">
        <h3 className="text-sm font-bold text-white mb-2">Maintenance</h3>
        <p className="text-[11px] text-slate-500 mb-3">
          Si vous voyez une ancienne version de l'interface après un déploiement, videz le cache local et rechargez.
          Le système détecte normalement les nouvelles versions automatiquement et propose un rechargement.
        </p>
        <button
          onClick={() => {
            if (window.confirm("Vider le cache local et recharger la page ?")) {
              forceClearCacheAndReload();
            }
          }}
          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-[#f7c935]/10 text-[#f7c935] font-bold border border-[#f7c935]/20 hover:bg-[#f7c935]/20 transition-colors"
          data-testid="btn-force-cache-reload"
        >
          Vider le cache et recharger
        </button>
      </div>
    </div>
  );
}
